import { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import type { MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode } from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import { Banner, PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { subtitleFiltersWithCustomColors, useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import DurationService from "app/services/DurationService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { allTrackers } from "app/services/track/track";

import { Divider } from "../../components/Divider";
import { useKeywordListsNodeAPIQueryParams } from "../../hooks/useKeywordListsNodeAPIQueryParams";
import { useMonthToDateToggle } from "../../hooks/useMonthToDateToggle";
import { queryOptionsBuilder } from "../../utils/queryOptionsBuilder";
import type { CompareMetricResponse } from "../search-trends-page/common/types";
import type { IRawDataMetrics } from "../search-trends-page/demand-size/types";

import { getMostRecentSpikeDate } from "./getMostRecentSpikeDate";
import { SearchVolumeOverTimeChangeMetrics } from "./SearchVolumeOverTimeChangeMetrics";
import { SearchVolumeOverTimeChart } from "./SearchVolumeOverTimeChart";
import { SearchVolumeOverTimeColumnChart } from "./SearchVolumeOverTimeColumnChart";
import { selectSearchVolumeData } from "./searchVolumeOverTimeSelectors";
import { useAnomalyDates } from "./useAnomalyDates";
import { useDemandTrendsSpikeAnalysis } from "./useDemandTrendsSpikeAnalysis";

const WidgetContainerWrapper = styled(WidgetContainer)`
    width: 100%;
    overflow: clip;
    margin-bottom: 24px;
`;

const DEMAND_SPIKE_AGENT_BANNER_ENABLED_PREFERENCE_KEY = "demandSpikeAgentBannerEnabled";

const AIBanner = styled(Banner)`
    background: linear-gradient(
        90deg,
        rgba(112, 162, 255, 0.2) 0.69%,
        rgba(3, 194, 235, 0.2) 72.43%,
        rgba(75, 226, 190, 0.2) 99.61%
    );
    padding: 8px 24px;
    border-radius: 0;
`;

const SubmodulesContainer = styled.div`
    display: grid;
    grid-template-columns: 160px 1fr;
    grid-template-rows: 1fr;
    gap: 0px 24px;
    grid-template-areas: "left right";
    padding: 24px;
`;

const LeftPaneContainer = styled.div`
    grid-area: left;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 160px;
    height: 250px;
`;

const RightPaneContainer = styled.div`
    grid-area: right;
    height: 250px;
`;

export const SearchVolumeOverTimeWidget = () => {
    const t = useTranslation();
    const {
        params: { duration, comparedDuration },
    } = useCurrentStateAndParams();
    const { isWindow } = DurationService.getDurationData(duration, comparedDuration).forAPI;
    const isPeriodOverPeriod = DurationService.isPeriodOverPeriod();

    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");
    const enrichedSubtitleFilters = isPeriodOverPeriod
        ? subtitleFiltersWithCustomColors(subtitleFilters, colorsPalettes.blue[400], colorsPalettes.orange[400])
        : subtitleFilters;
    const [showBanner, setShowBanner] = useState(
        PreferencesService.get(DEMAND_SPIKE_AGENT_BANNER_ENABLED_PREFERENCE_KEY) ?? true,
    );
    const { isMonthToDateActive, onOffSwitchConfig } = useMonthToDateToggle({
        eventSubName: "Search Volume Over Time",
        mtdParam: "searchVolumeMTD",
    });

    const select = useMemo(() => {
        return (data: MarketResearchDemandAnalysisCalculationModelsSearchTrendsNode) => {
            return selectSearchVolumeData(data);
        };
    }, [isPeriodOverPeriod]);
    const queryParams = useKeywordListsNodeAPIQueryParams({ isMonthToDate: isMonthToDateActive });
    const queryOptions = useMemo(
        () =>
            queryOptionsBuilder("DemandSearchTrends", "marketResearchDemandAnalysisSearchTrendsList")
                .setSelect(select)
                .setArgs(queryParams)
                .build(),
        [queryParams, select],
    );
    const { data, isLoading, isError } = useQuery(queryOptions);
    const { totalData, chartData, categories, chartTooltipData } = data ?? {
        totalData: null,
        chartData: [],
        categories: [],
        chartTooltipData: [],
    };

    const anomalyDates = useAnomalyDates();
    const { analyzeSpike } = useDemandTrendsSpikeAnalysis();

    const mostRecentSpikeDate = useMemo(() => getMostRecentSpikeDate(anomalyDates), [anomalyDates]);

    const analyzeButtonConfig = useMemo(
        () =>
            mostRecentSpikeDate
                ? [
                      {
                          type: "primary" as const,
                          label: t("demand_analysis.search_volume_over_time.analyze.button.label"),
                          onClick: () => analyzeSpike(mostRecentSpikeDate),
                      },
                  ]
                : [],
        [mostRecentSpikeDate, analyzeSpike, t],
    );

    const handleCloseBanner = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Demand Spike Agent Banner/Remove Banner");
        void PreferencesService.add({ [DEMAND_SPIKE_AGENT_BANNER_ENABLED_PREFERENCE_KEY]: false });
        setShowBanner(false);
    };

    return (
        <WidgetContainerWrapper
            automationId="search-trends-overview-widget"
            noData={isError}
            title={t("demand.analysis.search.trends.trends.overview.header.title")}
            tooltip={t("demand.analysis.search.trends.demand.over.time.volume.over.time.header.title.tooltip")}
            subtitleFilters={enrichedSubtitleFilters}
            onOffSwitchConfig={onOffSwitchConfig}
            dynamicButtonsConfig={analyzeButtonConfig}
        >
            {showBanner && anomalyDates != null && anomalyDates.length > 0 && !isWindow ? (
                <AIBanner
                    title={
                        <Text fontVariant="body1">
                            {t("demand.analysis.search.trends.trends.overview.aibanner.title")}
                        </Text>
                    }
                    CustomIcon={
                        <SWReactIcons
                            iconName="sparkles-ai-2"
                            size="sm"
                            iconColor={colorsPalettes.carbon["400"]}
                        />
                    }
                    onCloseClick={handleCloseBanner}
                />
            ) : (
                <Divider />
            )}

            <SubmodulesContainer>
                <LeftPaneContainer>
                    {totalData && "Values" in totalData.Volume ? (
                        <SearchVolumeOverTimeColumnChart
                            isLoading={isLoading}
                            data={totalData as CompareMetricResponse}
                        />
                    ) : (
                        <SearchVolumeOverTimeChangeMetrics
                            isLoading={isLoading}
                            data={totalData as IRawDataMetrics}
                        />
                    )}
                </LeftPaneContainer>
                <RightPaneContainer>
                    {isLoading ? (
                        <PixelPlaceholderLoader
                            width="100%"
                            height={250}
                        />
                    ) : (
                        <SearchVolumeOverTimeChart
                            anomalyDates={anomalyDates ?? []}
                            chartData={chartData}
                            categories={categories}
                            chartTooltipData={chartTooltipData}
                        />
                    )}
                </RightPaneContainer>
            </SubmodulesContainer>
        </WidgetContainerWrapper>
    );
};
